import { inject, Injectable, OnDestroy, signal } from "@angular/core";
import {
  Observable,
  BehaviorSubject,
  of,
  Subscription,
} from "rxjs";
import { map, catchError, switchMap, finalize, tap, take } from "rxjs/operators";
import { AuthKkuWorkloadSync, AuthUserResponse, SelectedRole } from "../models/user.model";
import { AuthState } from "../models/auth.model";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import {
  ERP_LEAVE_IMPORT_ROLE_IDS,
  RoleID,
  resolveRoleId,
  SUBJECT_MANAGEMENT_ROLE_IDS,
  SUBJECT_SCHEDULE_ROLE_IDS,
  TEACH_COMPENSATION_ROLE_IDS,
} from "src/utils/enum/role.enum";
import { ROLE_PERMISSION_MAP } from "src/utils/enum/role-permission.map";
import { Permission } from "src/utils/enum/permission.enum";
import { NgxPermissionsService } from "ngx-permissions";
import { SweetAlertService } from "src/app/services/sweet-alert-service/sweet-alert.service";
import { environment } from "src/environments/environment";

export type UserType = AuthUserResponse | undefined;

const RETURN_URL_STORAGE_KEY = "auth.pendingReturnUrl";
export const POST_AUTH_RETURN_URL_STATE_KEY = "auth.postAuthReturnUrl";

// A safe returnUrl is an internal, relative in-app path only — never a
// scheme-relative/absolute URL (open-redirect guard: rejects "//evil.com"
// and "https://evil.com") and never back into the auth flow itself (would
// either loop or be a meaningless "resume" target once login succeeds).
// Exported standalone (rather than a private method) so it's directly unit
// testable without instantiating AuthService.
export function isSafeReturnUrl(url: string | null | undefined): url is string {
  if (typeof url !== "string" || url.trim() === "") {
    return false;
  }

  if (!url.startsWith("/") || url.startsWith("//")) {
    return false;
  }

  if (url.startsWith("/auth") || url.startsWith("/select-role")) {
    return false;
  }

  return true;
}

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy {
  currentUser$: Observable<UserType>;
  currentUser = signal<AuthUserResponse | null>(null);
  selectedRoles = signal<SelectedRole[]>([]);
  selectedRole = signal<SelectedRole | null>(null);
  role = signal<number | null>(null);
  activeKkuWorkloadSync = signal<AuthKkuWorkloadSync | null>(null);

  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;
  public currentToken: string | null;

  AuthStates = AuthState;
  authState = signal<any>(this.AuthStates.UNAUTHORIZED);

  private unsubscribe: Subscription[] = [];
  private unauthorizedAlertInProgress = false;
  private excepted_key_names: string[] = [];

  #http = inject(HttpClient);
  #sweetAlertService = inject(SweetAlertService);
  #router = inject(Router);
  #permissionsService = inject(NgxPermissionsService);

  constructor() {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    // const subscr = this.getUserByToken().subscribe();
    // this.unsubscribe.push(subscr);
  }

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  _setFormData(object_key: string, value: any) {
    const formData = new FormData();
    for (const key in value) {
      const obj = value[key];
      if (Array.isArray(obj)) {
        obj.forEach((e, i) => {
          if (this.excepted_key_names.includes(key)) {
            formData.append(`${object_key}[${key}][]`, e);
          } else {
            for (const key_i in e) {
              formData.append(`${object_key}[${key}][][${key_i}]`, e[key_i]);
            }
          }
        });
      } else {
        formData.append(`${object_key}[${key}]`, value[key]);
      }
    }
    return formData;
  }

  setUser(user: AuthUserResponse): void {
    this.currentUser.set(user);
    this.currentUserSubject.next(user);
  }

  syncSession(response: AuthUserResponse): AuthUserResponse {
    this.activeKkuWorkloadSync.set(response.active_kku_workload_sync ?? null);
    this.setUser(response);

    if (response.active_role) {
      this.setSelectedRole(response.active_role);
    } else {
      this.clearSelectedRole();
    }

    return response;
  }

  getSelectedRoles(): SelectedRole[] {
    const selectedRole = this.getSelectedRole();
    return selectedRole ? [selectedRole] : [];
  }

  setSelectedRoles(roles: SelectedRole[]): void {
    this.setSelectedRole(roles[0] ?? null);
  }

  clearSelectedRoles(): void {
    this.clearSelectedRole();
  }

  getSelectedRole(): SelectedRole | null {
    return this.selectedRole();
  }

  isActiveAdminRole(user: UserType | null = this.currentUserValue ?? this.currentUser()): boolean {
    return this.getActiveRoleId(user) === RoleID.ADMIN;
  }

  getDefaultAuthenticatedUrl(user: UserType | null = this.currentUserValue ?? this.currentUser()): string {
    const activeRoleId = this.getActiveRoleId(user);

    if (this.isActiveAdminRole(user)) {
      return "/settings/study-period";
    }

    if (
      activeRoleId != null &&
      SUBJECT_MANAGEMENT_ROLE_IDS.some((roleId) => roleId === activeRoleId)
    ) {
      return "/subject-management";
    }

    if (
      activeRoleId != null &&
      SUBJECT_SCHEDULE_ROLE_IDS.some((roleId) => roleId === activeRoleId)
    ) {
      return "/schedule-submissions-management/schedule-submissions";
    }

    if (
      activeRoleId != null &&
      TEACH_COMPENSATION_ROLE_IDS.some((roleId) => roleId === activeRoleId)
    ) {
      return "/teach-compensation-management/teach-compensation";
    }

    if (
      activeRoleId != null &&
      environment.enableErpLeaveImport &&
      ERP_LEAVE_IMPORT_ROLE_IDS.some((roleId) => roleId === activeRoleId)
    ) {
      return "/teach-compensation-management/erp-leave-import";
    }

    // Disbursement Document Certification (ขั้นตอนการรับรองเอกสารส่งเบิก) — v1.1.
    // Document officer is view/export-only (no REG subsystem — see
    // context/business_flow/DisbursementDocumentFlow.md), so their default
    // landing is the department registry rather than a certification queue.
    // Release 1.1 hides disbursement — finance_officer has no other page
    // (falls to 403); document_officer still has teach-compensation-report.
    if (activeRoleId === RoleID.FINANCE_OFFICER && environment.enableDisbursement) {
      return "/disbursement-management/disbursement-certification";
    }

    if (activeRoleId === RoleID.DOCUMENT_OFFICER) {
      return environment.enableDisbursement
        ? "/disbursement-management/registry"
        : "/teach-compensation-management/teach-compensation-report";
    }

    return "/error/403";
  }

  setSelectedRole(role: SelectedRole | null): void {
    if (!role) {
      this.clearSelectedRole();
      return;
    }

    const normalizedRoleId = resolveRoleId(role) ?? Number(role.id);
    const normalizedRole = {
      ...role,
      id: normalizedRoleId,
    };

    this.selectedRole.set(normalizedRole);
    this.selectedRoles.set([normalizedRole]);
    this.setUserRole(normalizedRoleId);
  }

  clearSelectedRole(): void {
    this.selectedRole.set(null);
    this.selectedRoles.set([]);
  }

  setUserRole(roleId: number) {
    const normalizedRoleId = Number(roleId);
    this.role.set(normalizedRoleId);
    const permissionMap: Partial<Record<number, Permission[]>> = ROLE_PERMISSION_MAP;
    const permissions = permissionMap[normalizedRoleId] ?? [];
    this.#permissionsService.flushPermissions();
    this.#permissionsService.loadPermissions(permissions);
  }

  // public methods
  login(payload: { email: string }): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.#http.post<AuthUserResponse>("/session", payload).pipe(
      tap((res) => {
        this.syncSession(res);
        return res;
      }),
      // switchMap(() => this.getUserByToken()),
      // switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error("err", err);
        return of(undefined);
      }),
      finalize(() =>
        setTimeout(() => {
          this.isLoadingSubject.next(false);
        }, 500),
      ),
    );
  }

  loginWithSso(): void {
    window.location.href = environment.ssoLoginUrl;
  }

  completeSsoLogin(code: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.#http.post<AuthUserResponse>("/session/sso/callback", { code }).pipe(
      tap((res) => {
        this.syncSession(res);
        return res;
      }),
      catchError((err) => {
        console.error("sso err", err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  logout(): void {
    this.#http.delete("/session").pipe(
      take(1),
      catchError(() => of(null)),
      finalize(() => {
        this.clearSession();
        void this.#router.navigate(["/auth/login"]);
      }),
    ).subscribe();
  }

  verifyRole(roleId: number): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.#http.post<AuthUserResponse>("/session/verify", { role_id: roleId }).pipe(
      tap((res) => {
        this.syncSession(res);
        return res;
      }),
      catchError((err) => {
        console.error("err", err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  switchRole(roleId: number): Observable<AuthUserResponse> {
    this.isLoadingSubject.next(true);
    return this.#http.post<AuthUserResponse>("/session/verify", { role_id: roleId }).pipe(
      tap((res) => {
        this.syncSession(res);
        return res;
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  // Deep-link returnUrl persistence (Phase F, gap #1). sessionStorage is the
  // ONLY carrier that survives the SSO round-trip: loginWithSso() does a
  // hard `window.location.href` navigation away from the app, and the SSO
  // provider redirects back to the app's root with just `?code=` — no way to
  // thread a returnUrl through the provider itself, and no in-memory Router
  // state survives a full page reload. AuthGuard calls persistReturnUrl()
  // whenever it redirects an unauthenticated/role-less user to
  // login/select-role; LoginComponent also calls it defensively from its own
  // `returnUrl` query param (covers a direct hit on /auth/login?returnUrl=…
  // that never went through the guard). navigateAfterAuthentication() is the
  // single final "enter the app" path; it consumes this value only after a
  // role is resolved and marks that navigation so AuthGuard can fall back to
  // the role's default page when the deep link belongs to another role.
  persistReturnUrl(url: string | null | undefined): void {
    if (!isSafeReturnUrl(url)) {
      return;
    }

    try {
      sessionStorage.setItem(RETURN_URL_STORAGE_KEY, url);
    } catch {
      // sessionStorage may be unavailable (privacy mode) — best-effort only.
    }
  }

  takeReturnUrl(): string | null {
    try {
      const stored = sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
      sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
      return isSafeReturnUrl(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  async navigateAfterAuthentication(user: UserType | null): Promise<boolean> {
    const returnUrl = this.takeReturnUrl();

    if (returnUrl != null) {
      return this.#router.navigateByUrl(returnUrl, {
        state: {
          [POST_AUTH_RETURN_URL_STATE_KEY]: returnUrl,
        },
      });
    }

    return this.#router.navigateByUrl(this.getDefaultAuthenticatedUrl(user));
  }

  async handleUnauthorized(): Promise<void> {
    if (this.unauthorizedAlertInProgress) {
      return;
    }

    this.unauthorizedAlertInProgress = true;
    this.clearSession();

    await this.#sweetAlertService.swalAlert(
      "เซสชันหมดอายุ",
      "กรุณาเข้าสู่ระบบใหม่อีกครั้ง",
    );

    if (!this.#router.url.startsWith("/auth/login")) {
      await this.#router.navigate(["/auth/login"]);
    }

    this.unauthorizedAlertInProgress = false;
  }

  restoreSession(): Observable<UserType> {
    return this.#http.get<AuthUserResponse>("/session/verify").pipe(
      tap((res) => {
        this.syncSession(res);
        return res;
      }),
      catchError(() => {
        this.clearSession();
        return of(undefined);
      }),
    );
  }

  // need create new user then login
  registration(payload: Record<string, unknown>): Observable<any> {
    const registerType = payload["is_student"] === true ? "student" : "parent";
    const formData = this._setFormData(registerType, payload);
    this.isLoadingSubject.next(true);
    return this.#http.post(`/${registerType}s`, formData).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(payload as { email: string })),
      catchError((err) => {
        console.error("err", err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  forgotPassword(email: string) {}

  updateAuthState(state: AuthState) {
    this.authState.update((_) => state);
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  getActiveRoleId(user?: UserType | null): RoleID | null {
    const targetUser = user === undefined
      ? this.currentUser() ?? this.currentUserValue
      : user;

    return resolveRoleId(targetUser?.active_role);
  }

  private clearSession(): void {
    this.clearSelectedRoles();
    this.activeKkuWorkloadSync.set(null);
    this.currentUser.set(null);
    this.currentUserSubject.next(undefined);
    this.#permissionsService.flushPermissions();
  }
}
